import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../components/ui/use-toast';
import apiClient from '../client';
import { applicationCreateInputSchema, type ApplicationCreateInput } from '../schemas/application-create.schema';
import { applicationSchema, type Application } from '../schemas/application.schema';
import { billingAccountSchema, type BillingAccount } from '../schemas/billing-account.schema';
import { applicationConfigurationSchema, type ApplicationConfiguration } from '../schemas/configuration.schema';

export const CONFIGURATIONS_QUERY_KEY = 'configurations';

interface ListConfigurationsParams {
    appKey?: string;
}

export function useConfigurations(params?: ListConfigurationsParams, options?: { enabled: boolean }) {
    return useQuery({
        queryKey: [CONFIGURATIONS_QUERY_KEY, params],
        enabled: options?.enabled,
        queryFn: async () => {
            const response = await apiClient.get<ApplicationConfiguration[]>('/configurations', { params });
            return response.data.map((config) => applicationConfigurationSchema.parse(config));
        },
    });
}

export function useConfiguration(identifier: string) {
    return useQuery({
        queryKey: [CONFIGURATIONS_QUERY_KEY, identifier],
        queryFn: async () => {
            const response = await apiClient.get<ApplicationConfiguration>(`/configurations/${identifier}`);
            return applicationConfigurationSchema.parse(response.data);
        },
        enabled: !!identifier,
    });
}

export function useBillingAccount(accountId: string | undefined) {
    return useQuery({
        queryKey: [CONFIGURATIONS_QUERY_KEY, 'billing', accountId],
        queryFn: async () => {
            if (!accountId) {
                throw new Error('accountId is required');
            }
            const response = await apiClient.get<BillingAccount>(`/configurations/billing/accounts/${accountId}`);
            return billingAccountSchema.parse(response.data);
        },
        enabled: !!accountId,
        retry: false,
    });
}

interface LockConfigurationParams {
    identifier: string;
    locked: boolean;
    lockReason?: string;
}

export function useLockConfiguration() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ identifier, locked, lockReason }: LockConfigurationParams) => {
            const response = await apiClient.post<ApplicationConfiguration>(`/configurations/${identifier}/lock`, {
                locked,
                lockReason,
            });
            return applicationConfigurationSchema.parse(response.data);
        },
        onSuccess: (data) => {
            void queryClient.invalidateQueries({ queryKey: [CONFIGURATIONS_QUERY_KEY] });
            toast({
                title: 'Success',
                description: `Brand ${data.locked ? 'locked' : 'unlocked'} successfully`,
            });
        },
    });
}

export function useCreateConfiguration() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: ApplicationCreateInput) => {
            const validatedInput = applicationCreateInputSchema.parse(input);

            const response = await apiClient.post<Application>('/applications', validatedInput);
            return applicationSchema.parse(response.data);
        },
        onSuccess: (data) => {
            void queryClient.invalidateQueries({ queryKey: [CONFIGURATIONS_QUERY_KEY] });
            toast({
                title: 'Success',
                description: `Brand "${data.name}" created successfully`,
            });
        },
        onError: (error: Error) => {
            toast({
                variant: 'destructive',
                title: 'Error',
                description: error.message || 'Failed to create brand',
            });
        },
    });
}
